import { categoryUrl } from "@saleor/categories/urls";
import { collectionUrl } from "@saleor/collections/urls";
import ActionDialog from "@saleor/components/ActionDialog";
import AssignProductDialog from "@saleor/components/AssignProductDialog";
import { RecommendProductTab } from "@saleor/components/RecommendProductSection";
import { DEFAULT_INITIAL_SEARCH_DATA } from "@saleor/config";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import {
  useAssignRecommendProductMutation,
  useUnassignRecommendProductMutation
} from "@saleor/products/mutations";
import { productUrl } from "@saleor/products/urls";
import useProductSearch from "@saleor/searches/useProductSearch";
import {
  RecommendationTargetTypeEnum,
  RecommendationTypeEnum
} from "@saleor/types/globalTypes";
import { mapEdgesToItems } from "@saleor/utils/maps";
import React from "react";
import { useIntl } from "react-intl";

interface RecommendProductDialogProps {
  targetId: string;
  targetType: RecommendationTargetTypeEnum;
  closeModal: () => void;
  reset: () => void;
  action: string;
  ids: string[];
}

const RecommendProductDialog: React.FC<RecommendProductDialogProps> = props => {
  const { targetId, targetType, closeModal, reset, action, ids } = props;

  const navigate = useNavigator();
  const notify = useNotifier();
  const intl = useIntl();

  const { search, loadMore, result } = useProductSearch({
    variables: DEFAULT_INITIAL_SEARCH_DATA
  });

  const [
    assignRecommend,
    assignRecommendOpts
  ] = useAssignRecommendProductMutation({
    onCompleted: data => {
      if (data.recommendProductAdd.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Added recommend product to product."
          })
        });
        if (targetType === RecommendationTargetTypeEnum.PRODUCT) {
          navigate(productUrl(targetId), true);
        } else if (targetType === RecommendationTargetTypeEnum.CATEGORY) {
          navigate(categoryUrl(targetId), true);
        } else {
          navigate(collectionUrl(targetId), true);
        }
      }
    }
  });

  const [
    assignAlsolike,
    assignAlsoliketOpts
  ] = useAssignRecommendProductMutation({
    onCompleted: data => {
      if (data.recommendProductAdd.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Added recommend product to product."
          })
        });
        if (targetType === RecommendationTargetTypeEnum.PRODUCT) {
          navigate(
            productUrl(targetId, { activeTab: RecommendProductTab.also_likes }),
            true
          );
        } else if (targetType === RecommendationTargetTypeEnum.CATEGORY) {
          navigate(
            categoryUrl(targetId, {
              activeRecommendTab: RecommendProductTab.also_likes
            }),
            true
          );
        } else {
          navigate(
            collectionUrl(targetId, {
              activeTab: RecommendProductTab.also_likes
            }),
            true
          );
        }
      }
    }
  });

  const [
    assignbuyTogether,
    assignbuyTogetherOpts
  ] = useAssignRecommendProductMutation({
    onCompleted: data => {
      if (data.recommendProductAdd.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Added recommend product to product."
          })
        });
        if (targetType === RecommendationTargetTypeEnum.PRODUCT) {
          navigate(productUrl(targetId), true);
        } else if (targetType === RecommendationTargetTypeEnum.CATEGORY) {
          navigate(categoryUrl(targetId), true);
        } else {
          navigate(collectionUrl(targetId), true);
        }
      }
    }
  });

  const [
    unassignProduct,
    unassignProductOpts
  ] = useUnassignRecommendProductMutation({
    onCompleted: data => {
      if (data.recommendProductRemove.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Deleted recommend product from product."
          })
        });
        reset();
        closeModal();
      }
    }
  });

  return (
    <>
      <AssignProductDialog
        key="assign-recommend"
        confirmButtonState={assignRecommendOpts.status}
        hasMore={result.data?.search?.pageInfo.hasNextPage}
        open={action === "assign-recommend"}
        onFetch={search}
        onFetchMore={loadMore}
        loading={result.loading}
        onClose={closeModal}
        onSubmit={products =>
          assignRecommend({
            variables: {
              targetId,
              targetType,
              recommendType: RecommendationTypeEnum.RECOMMEND,
              productIds: products.map(product => product.id)
            }
          })
        }
        products={mapEdgesToItems(result?.data?.search)?.filter(
          suggestedProduct => suggestedProduct.id
        )}
      />
      <AssignProductDialog
        key="assign-alsolike"
        confirmButtonState={assignAlsoliketOpts.status}
        hasMore={result.data?.search?.pageInfo.hasNextPage}
        open={action === "assign-alsolike"}
        onFetch={search}
        onFetchMore={loadMore}
        loading={result.loading}
        onClose={closeModal}
        onSubmit={products =>
          assignAlsolike({
            variables: {
              targetId,
              targetType,
              recommendType: RecommendationTypeEnum.ALSO_LIKE,
              productIds: products.map(product => product.id)
            }
          })
        }
        products={mapEdgesToItems(result?.data?.search)?.filter(
          suggestedProduct => suggestedProduct.id
        )}
      />
      <AssignProductDialog
        key="assign-buytogether"
        confirmButtonState={assignbuyTogetherOpts.status}
        hasMore={result.data?.search?.pageInfo.hasNextPage}
        open={action === "assign-buytogether"}
        onFetch={search}
        onFetchMore={loadMore}
        loading={result.loading}
        onClose={closeModal}
        onSubmit={products =>
          assignbuyTogether({
            variables: {
              targetId,
              targetType,
              recommendType: RecommendationTypeEnum.BUY_TOGETHER,
              productIds: products.map(product => product.id)
            }
          })
        }
        products={mapEdgesToItems(result?.data?.search)?.filter(
          suggestedProduct => suggestedProduct.id
        )}
      />
      <ActionDialog
        key="unassign-recommend"
        confirmButtonState={unassignProductOpts.status}
        onClose={closeModal}
        onConfirm={() =>
          unassignProduct({
            variables: {
              targetId,
              targetType,
              recommendType: RecommendationTypeEnum.RECOMMEND,
              productIds: ids
            }
          })
        }
        open={action === "unassign-recommend"}
        title={intl.formatMessage({
          defaultMessage:
            "Are you sure you want to delete this product in recommend?",
          description: "dialog title"
        })}
      />
      <ActionDialog
        key="unassign-alsolike"
        confirmButtonState={unassignProductOpts.status}
        onClose={closeModal}
        onConfirm={() =>
          unassignProduct({
            variables: {
              targetId,
              targetType,
              recommendType: RecommendationTypeEnum.ALSO_LIKE,
              productIds: ids
            }
          })
        }
        open={action === "unassign-alsolike"}
        title={intl.formatMessage({
          defaultMessage:
            "Are you sure you want to delete this product in also like?",
          description: "dialog title"
        })}
      />
      <ActionDialog
        key="unassign-buytogether"
        confirmButtonState={unassignProductOpts.status}
        onClose={closeModal}
        onConfirm={() =>
          unassignProduct({
            variables: {
              targetId,
              targetType,
              recommendType: RecommendationTypeEnum.BUY_TOGETHER,
              productIds: ids
            }
          })
        }
        open={action === "unassign-buytogether"}
        title={intl.formatMessage({
          defaultMessage:
            "Are you sure you want to delete this product in also like?",
          description: "dialog title"
        })}
      />
    </>
  );
};

RecommendProductDialog.displayName = "RecommendProductDialog";
export default RecommendProductDialog;
